import { useCallback, useEffect, useState } from 'react';

import { usePrevious } from 'react-use';

import { useList, useNftDataStream } from '@pkg/hooks';
import { type BaseCurrencyType } from '@pkg/types';
import { assertNumber } from '@pkg/utils';

import { useExchangeRatesStore, useListStore } from '@app/store/zustand';

import { type InputPriceType } from './AssetListingSheet.types';
import { filterInputPrice, formatInputPrice } from './AssetListingSheet.utilities';

export function useAssetListingState() {
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const { confirmListing } = useList();
    const listedToken = useListStore((state) => state.token);
    const removeToken = useListStore((state) => state.removeToken);
    const setPendingConfirmation = useListStore((state) => state.setPendingConfirmation);

    const { loading, nativeCurrency, appraisal, floorData, lastSaleData, errors } = useNftDataStream({
        contractAddress: listedToken?.collection?.contractAddress,
        tokenId: listedToken?.tokenId,
    });

    const [localCurrency, setLocalCurrency] = useState<BaseCurrencyType>('usd');
    const prevLocalCurrency = usePrevious(localCurrency);

    const [localPrice, setLocalPrice] = useState<InputPriceType>();
    const [nativePrice, setNativePrice] = useState<number>();
    const [usdPrice, setUsdPrice] = useState(0);
    const [localAppraisal, setLocalAppraisal] = useState<number>(0);
    const [allMarketplaces, setAllMarketplaces] = useState(false);

    // TODO: Waiting on backend before these features can be completed.
    const [creatorFeeUsd, setCreatorFeeUsd] = useState(0);
    const [processFeeUsd, setProcessFeeUsd] = useState(0);
    const [estimationUsd, setEstimationUsd] = useState(0);

    const appraisedEth = appraisal.ethValue;

    const formatAndUpdateLocalPrice = useCallback((value?: InputPriceType, currencyType: BaseCurrencyType = 'usd') => {
        const newValue = formatInputPrice(value, currencyType);
        setLocalPrice(newValue);
    }, []);

    function handlePriceChange(value?: InputPriceType) {
        setLocalPrice(value);
    }

    function handleAllMarketplacesToggle() {
        setAllMarketplaces((current) => !current);
    }

    async function handleSubmit() {
        const confirmedPrice = nativePrice ?? 0;
        setPendingConfirmation(true);

        return confirmListing(confirmedPrice, allMarketplaces);
    }

    function handleReset() {
        removeToken();

        // No need to reset `usdPrice` or `localAppraisal`,
        // as they will be reset upon `localPrice` change.
        setLocalCurrency('usd');
        setLocalPrice(undefined);
        setNativePrice(undefined);
        setAllMarketplaces(false);

        setCreatorFeeUsd(0);
        setProcessFeeUsd(0);
        setEstimationUsd(0);
    }

    // Tracking conversion of localAppraisal.
    useEffect(() => {
        setLocalAppraisal(convertCurrency(nativeCurrency, localCurrency, appraisedEth, true));
    }, [convertCurrency, nativeCurrency, localCurrency, appraisedEth]);

    // Tracking conversion of localPrice when localCurrency is changed.
    useEffect(() => {
        if (assertNumber(localPrice) && prevLocalCurrency && prevLocalCurrency !== localCurrency) {
            const convertedPrice = convertCurrency(prevLocalCurrency, localCurrency, localPrice, true);

            formatAndUpdateLocalPrice(convertedPrice, localCurrency);
        }
    }, [convertCurrency, prevLocalCurrency, localCurrency, localPrice, formatAndUpdateLocalPrice]);

    // Tracking conversion of nativePrice when localPrice updates.
    useEffect(() => {
        const filtered = filterInputPrice(localPrice);
        const fromCurrency =
            prevLocalCurrency && prevLocalCurrency !== localCurrency ? prevLocalCurrency : localCurrency;
        const newValue =
            filtered === undefined ? undefined : convertCurrency(fromCurrency, nativeCurrency, filtered, true);

        setNativePrice(newValue);
    }, [convertCurrency, prevLocalCurrency, localCurrency, nativeCurrency, localPrice]);

    // Tracking a constant usdPrice conversion.
    useEffect(() => {
        const newValue = assertNumber(localPrice) ? convertCurrency(localCurrency, 'usd', localPrice, true) : 0;

        setUsdPrice(newValue + creatorFeeUsd + processFeeUsd);
    }, [convertCurrency, localCurrency, localPrice, creatorFeeUsd, processFeeUsd]);

    // Tracking estimationUsd as requisite values update.
    useEffect(() => {
        setEstimationUsd(usdPrice + creatorFeeUsd + processFeeUsd);
    }, [usdPrice, creatorFeeUsd, processFeeUsd]);

    return {
        errors,
        loading,
        listedToken,
        removeToken,
        nativeCurrency,
        floorUsdValue: floorData.usdValue,
        floorUsd24hChange: floorData.usd24hChange,
        floorPercentChange: floorData.percentChange,
        lastSaleUsd: lastSaleData.usdValue,
        lastSaleUtcMs: lastSaleData.utcMs,
        localCurrency,
        localAppraisal,
        localPrice,
        nativePrice,
        usdPrice,
        appraisedEth,
        allMarketplaces,
        creatorFeeUsd,
        processFeeUsd,
        estimationUsd,
        formatAndUpdateLocalPrice,
        handlePriceBlur: () => formatAndUpdateLocalPrice(localPrice, localCurrency),
        handlePriceChange,
        handleLocalCurrencyChange: setLocalCurrency,
        handleAllMarketplacesToggle,
        handleSubmit,
        handleReset,
    };
}
